import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { AppointmentForm, AppointmentView, type Draft, type Prefill, type StaffOption } from '../components/calendar/AppointmentPanel';
import { AffectedAppointments, BlockForm, BlockView, type Affected } from '../components/calendar/BlockPanel';
import { DayColumn } from '../components/calendar/DayColumn';
import { useWaitlist, WaitlistList } from '../components/calendar/WaitlistPanel';
import { TimeGrid, type GridBlock, type GridColumn, type GridItem, type GridPreview, type MinuteRange, type MoveRequest } from '../components/calendar/TimeGrid';
import { SidePanel } from '../components/forms';
import { Button, EmptyState, ErrorNote } from '../components/ui';
import { api, ApiError, type Salon } from '../lib/api';
import {
  moveInCache,
  refreshCalendar,
  useAppointment,
  useCalendar,
  useLiveCalendar,
  type AppointmentDetail,
  type CalendarAppointment,
  type CalendarBlock,
  type CalendarData,
  type CustomerSummary,
} from '../lib/calendar';
import { useLocale, useT, type TKey } from '../lib/i18n';
import { addDays, dateOf, formatDateLong, formatDateShort, formatTime, formatWeek, hhmm, instantAt, minuteOfDay, SALON_TZ, todayIn, weekDates } from '../lib/time';
import { useFirstLocation } from './setup-hours';

export type AgendaSearch = { datum?: string; weergave?: 'dag' | 'week' | 'lijst'; wie?: string; nieuw?: 1; afspraak?: string; klant?: string };

export function validateAgendaSearch(s: Record<string, unknown>): AgendaSearch {
  const datum = typeof s.datum === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(s.datum) ? s.datum : undefined;
  const weergave = s.weergave === 'week' || s.weergave === 'lijst' || s.weergave === 'dag' ? s.weergave : undefined;
  const wie = typeof s.wie === 'string' && /^[0-9a-f-]{36}$/.test(s.wie) ? s.wie : undefined;
  const afspraak = typeof s.afspraak === 'string' && /^[0-9a-f-]{36}$/.test(s.afspraak) ? s.afspraak : undefined;
  const klant = typeof s.klant === 'string' && /^[0-9a-f-]{36}$/.test(s.klant) ? s.klant : undefined;
  return { datum, weergave, wie, nieuw: s.nieuw === 1 || s.nieuw === '1' ? 1 : undefined, afspraak, klant };
}

type Panel =
  | { mode: 'create'; tab: 'appointment' | 'block'; prefill: Prefill }
  | { mode: 'view'; id: string }
  | { mode: 'edit'; id: string }
  | { mode: 'block'; block: CalendarBlock }
  | { mode: 'affected'; appointments: Affected[] }
  | { mode: 'waitlist' };

type Toast = { id: number; message: string; undo?: () => void; secondary?: { label: string; run: () => void } };

function useIsPhone() {
  return useSyncExternalStore(
    (cb) => {
      const mq = window.matchMedia('(max-width: 767px)');
      mq.addEventListener('change', cb);
      return () => mq.removeEventListener('change', cb);
    },
    () => window.matchMedia('(max-width: 767px)').matches,
  );
}

/** Current time, refreshed every minute (for the "now" line). */
function useNow() {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 60_000);
    return () => window.clearInterval(id);
  }, []);
  return now;
}

/** Minutes of `iso` relative to local midnight of `date` (can exceed 1440 for late nights). */
function minuteIn(iso: string, date: string, tz: string) {
  const d = dateOf(iso, tz);
  const m = minuteOfDay(iso, tz);
  if (d === date) return m;
  return d > date ? m + 1440 : m - 1440;
}

function rangesOnDate(ranges: Array<{ start: string; end: string }>, date: string, tz: string): MinuteRange[] {
  const dayStart = Date.parse(instantAt(date, 0, tz));
  const dayEnd = Date.parse(instantAt(addDays(date, 1), 0, tz));
  return ranges
    .filter((r) => Date.parse(r.start) < dayEnd && Date.parse(r.end) > dayStart)
    .map((r) => ({ start: Math.max(0, minuteIn(r.start, date, tz)), end: Math.min(1440, Date.parse(r.end) >= dayEnd ? 1440 : minuteIn(r.end, date, tz)) }));
}

export function AgendaPage() {
  const { orgId } = useParams({ from: '/s/$orgId' });
  const search = useSearch({ from: '/s/$orgId/agenda' });
  const navigate = useNavigate({ from: '/s/$orgId/agenda' });
  const t = useT();
  const locale = useLocale();
  const qc = useQueryClient();
  const isPhone = useIsPhone();
  const now = useNow();
  useLiveCalendar(orgId);

  const salon = useQuery({ queryKey: ['salon', orgId], queryFn: () => api<Salon>(`/orgs/${orgId}`) });
  const role = salon.data?.role ?? 'staff';
  const ownStaffId = salon.data?.staffMemberId ?? null;
  const location = useFirstLocation(orgId);

  const tz = SALON_TZ;
  const today = todayIn(tz);
  const date = search.datum ?? today;
  const view = search.weergave ?? 'dag';
  const days = view === 'dag' ? [date] : weekDates(date);
  const cal = useCalendar(orgId, location.data?.id, days[0]!, days.at(-1)!);
  const data = cal.data;

  const setSearch = (next: Partial<AgendaSearch>) => navigate({ search: (s: AgendaSearch) => ({ ...s, ...next }), replace: true });
  const goDate = (d: string) => setSearch({ datum: d === today ? undefined : d });
  const step = (dir: 1 | -1) => goDate(addDays(date, (view === 'dag' ? 1 : 7) * dir));

  // Clients waiting for a time on this day (empty unless the salon has the waitlist on).
  const waitlist = useWaitlist(orgId, location.data?.id, date, view === 'dag');
  const waiting = waitlist.data ?? [];
  const [panel, setPanel] = useState<Panel | null>(null);
  const closePanel = useCallback(() => setPanel(null), []);
  // The appointment or blocked time being made, drawn in the calendar while the panel is open.
  const [draft, setDraft] = useState<Draft | null>(null);
  useEffect(() => {
    if (!panel || (panel.mode !== 'create' && panel.mode !== 'edit')) setDraft(null);
  }, [panel]);
  const [toast, setToast] = useState<Toast | null>(null);
  const showToast = useCallback((message: string, undo?: () => void, secondary?: Toast['secondary']) => setToast({ id: Date.now(), message, undo, secondary }), []);
  const [forceAsk, setForceAsk] = useState<{ message: string; retry: () => void } | null>(null);

  const staff: StaffOption[] = useMemo(() => data?.staff.map((s) => ({ id: s.id, displayName: s.displayName, color: s.color })) ?? [], [data]);

  // The person shown in week view and on phones.
  const person = useMemo(() => {
    if (!data) return undefined;
    if (search.wie && data.staff.some((s) => s.id === search.wie)) return search.wie;
    if (ownStaffId && data.staff.some((s) => s.id === ownStaffId)) return ownStaffId;
    return data.staff.find((s) => rangesOnDate(s.scheduled, date, tz).length > 0)?.id ?? data.staff[0]?.id;
  }, [data, search.wie, ownStaffId, date, tz]);

  // Who has a column in the day view: everyone working (or booked) that day, or the filter.
  const dayStaffIds = useMemo(() => {
    if (!data) return [];
    if (search.wie) return [search.wie];
    const busy = new Set(data.appointments.filter((a) => dateOf(a.startsAt, tz) === date).flatMap((a) => a.staffIds));
    return data.staff.filter((s) => rangesOnDate(s.scheduled, date, tz).length > 0 || busy.has(s.id)).map((s) => s.id);
  }, [data, search.wie, date, tz]);

  // "+" in the bottom bar (or "Afspraak maken" on a client) opens a new appointment.
  useEffect(() => {
    if (search.nieuw !== 1) return;
    const minute = Math.ceil(minuteOfDay(Date.now(), tz) / 15) * 15;
    const prefill: Prefill = { date: today, minute: Math.min(minute, 23 * 60 + 45), staffId: role === 'staff' ? ownStaffId : null };
    const klant = search.klant;
    setSearch({ nieuw: undefined, klant: undefined });
    if (!klant) {
      setPanel({ mode: 'create', tab: 'appointment', prefill });
      return;
    }
    void api<{ client: CustomerSummary }>(`/orgs/${orgId}/clients/${klant}`)
      .then((r) => setPanel({ mode: 'create', tab: 'appointment', prefill: { ...prefill, customer: r.client } }))
      .catch(() => setPanel({ mode: 'create', tab: 'appointment', prefill }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search.nieuw]);

  // Opening a specific appointment from elsewhere (Vandaag, clients).
  useEffect(() => {
    if (!search.afspraak) return;
    setPanel({ mode: 'view', id: search.afspraak });
    setSearch({ afspraak: undefined });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search.afspraak]);

  // Keyboard shortcuts, as in Google Calendar: T today, ← → previous/next, D/W/L views, N new.
  const keys = useRef({ step: (_: 1 | -1) => {}, today: () => {}, view: (_: AgendaSearch['weergave']) => {}, create: () => {} });
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey || panelOpenRef.current) return;
      const el = e.target as HTMLElement;
      if (el.closest('input, textarea, select, [contenteditable="true"]')) return;
      const k = keys.current;
      const map: Record<string, () => void> = {
        t: k.today,
        ArrowLeft: () => k.step(-1),
        ArrowRight: () => k.step(1),
        j: () => k.step(1),
        k: () => k.step(-1),
        d: () => k.view('dag'),
        w: () => k.view('week'),
        l: () => k.view('lijst'),
        n: k.create,
        c: k.create,
      };
      const action = map[e.key];
      if (action) {
        e.preventDefault();
        action();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  const panelOpenRef = useRef(false);
  panelOpenRef.current = !!panel || !!forceAsk;

  const handleMove = useCallback(
    (move: MoveRequest, force = false) => {
      const a = move.item.appointment;
      const startsAt = instantAt(move.column.date, move.start, tz);
      const previousStaff = a.staffIds[0]!;
      const staffChanged = move.column.staffId !== previousStaff;
      const oldDuration = move.item.end - move.item.start;
      const resized = move.duration !== oldDuration;
      const rollback = moveInCache(qc, orgId, a.id, { startsAt, durationMs: move.duration * 60_000, staffMemberId: staffChanged ? move.column.staffId : undefined });
      api<{ appointment: AppointmentDetail }>(`/orgs/${orgId}/appointments/${a.id}/move`, {
        method: 'POST',
        body: JSON.stringify({
          startsAt,
          staffMemberId: staffChanged ? move.column.staffId : undefined,
          durationMinutes: resized ? move.duration : undefined,
          version: a.version,
          force,
        }),
      })
        .then((r) => {
          refreshCalendar(qc, orgId, a.id);
          const name = staff.find((s) => s.id === move.column.staffId)?.displayName ?? '';
          const message = staffChanged
            ? t('toast.movedTo', { name, time: formatTime(startsAt, tz) })
            : resized && startsAt === a.startsAt
              ? t('toast.resized', { time: formatTime(r.appointment.endsAt, tz) })
              : t('toast.moved', { time: formatTime(startsAt, tz) });
          showToast(message, () => {
            api(`/orgs/${orgId}/appointments/${a.id}/move`, {
              method: 'POST',
              body: JSON.stringify({
                startsAt: a.startsAt,
                staffMemberId: staffChanged ? previousStaff : undefined,
                durationMinutes: resized ? oldDuration : undefined,
                version: r.appointment.version,
              }),
            })
              .then(() => {
                refreshCalendar(qc, orgId, a.id);
                showToast(t('toast.undone'));
              })
              .catch((e: unknown) => showToast(e instanceof ApiError ? e.message : t('error.generic')));
          }, a.customerName ? {
            label: t('toast.dontNotify'),
            run: () => {
              void api(`/orgs/${orgId}/appointments/${a.id}/skip-message`, { method: 'POST' }).then(() => showToast(t('toast.notNotified')));
            },
          } : undefined);
        })
        .catch((e: unknown) => {
          rollback();
          refreshCalendar(qc, orgId, a.id);
          if (e instanceof ApiError && e.code === 'slot_unavailable' && e.details?.canForce) {
            setForceAsk({ message: e.message, retry: () => handleMove(move, true) });
          } else {
            showToast(e instanceof ApiError ? e.message : t('error.generic'));
          }
        });
    },
    [orgId, qc, showToast, staff, t, tz],
  );

  if (location.isSuccess && !location.data) return <EmptyState title={t('nav.agenda')} body={t('cal.noLocation')} />;

  const openCreate = (prefill: Prefill) => setPanel({ mode: 'create', tab: 'appointment', prefill });
  const newAppointment = () => {
    const minute = date === today ? Math.ceil(minuteOfDay(Date.now(), tz) / 15) * 15 : 10 * 60;
    openCreate({ date, minute: Math.min(minute, 23 * 60 + 45), staffId: view === 'dag' && !isPhone ? (search.wie ?? null) : (person ?? null) });
  };

  keys.current = {
    step,
    today: () => goDate(today),
    view: (v) => setSearch({ weergave: v === 'dag' ? undefined : v }),
    create: newAppointment,
  };

  const title =
    view === 'dag' ? formatDateLong(date, locale) : formatWeek(date, locale);

  return (
    <div className="flex w-full flex-col gap-4">
      {/* The date is the title, like the page of a book; tap it to pick another day. */}
      <header className="relative flex flex-col gap-4 pt-1">
        <div className="relative flex flex-wrap items-end justify-between gap-3">
          <label className="relative min-w-0 cursor-pointer">
            <span className="eyebrow block">{date === today ? t('cal.today') : t('nav.agenda')}</span>
            <h1 className="page-title mt-2 truncate first-letter:uppercase">{title}</h1>
            <input
              type="date"
              aria-label={t('cal.pickDate')}
              value={date}
              onChange={(e) => e.target.value && goDate(e.target.value)}
              className="absolute inset-0 cursor-pointer opacity-0"
            />
          </label>
          {/* Phones use the "+" in the bottom bar. */}
          <div className="hidden md:block">
            <Button onClick={newAppointment}>+ {t('cal.newAppointment')}</Button>
          </div>
        </div>
        <div className="relative flex flex-wrap items-center gap-2">
          <div className="flex items-center rounded-full border border-line bg-surface p-0.5 shadow-[0_1px_1px_rgb(2_53_42/0.04)]">
            <button aria-label={t('cal.prev')} className="flex size-9 items-center justify-center rounded-full text-lg hover:bg-subtle" onClick={() => step(-1)}>
              ‹
            </button>
            <button className="min-h-9 rounded-full px-3 text-sm font-medium hover:bg-subtle disabled:text-muted disabled:hover:bg-transparent" onClick={() => goDate(today)} disabled={date === today}>
              {t('cal.today')}
            </button>
            <button aria-label={t('cal.next')} className="flex size-9 items-center justify-center rounded-full text-lg hover:bg-subtle" onClick={() => step(1)}>
              ›
            </button>
          </div>
          <div role="tablist" className="flex rounded-full border border-line bg-surface p-0.5 shadow-[0_1px_1px_rgb(2_53_42/0.04)]">
            {(['dag', 'week', 'lijst'] as const).map((v) => (
              <button
                key={v}
                role="tab"
                aria-selected={view === v}
                onClick={() => setSearch({ weergave: v === 'dag' ? undefined : v })}
                className={`min-h-9 rounded-full px-4 text-sm font-medium ${view === v ? 'bg-ink text-canvas' : 'text-muted hover:text-ink'}`}
              >
                {t(`cal.view.${v === 'dag' ? 'day' : v === 'week' ? 'week' : 'list'}`)}
              </button>
            ))}
          </div>
          {data && data.staff.length > 1 && (!isPhone || view === 'week') && (
            <select
              aria-label={t('appt.staff')}
              value={view === 'week' ? (person ?? '') : (search.wie ?? '')}
              onChange={(e) => setSearch({ wie: e.target.value || undefined })}
              className="min-h-10 rounded-full border border-line bg-surface px-3 text-sm"
            >
              {view !== 'week' && <option value="">{t('cal.everyone')}</option>}
              {data.staff.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.displayName}
                </option>
              ))}
            </select>
          )}
          <select
            aria-label={t('cal.jump')}
            value=""
            onChange={(e) => e.target.value && goDate(addDays(date, Number(e.target.value) * 7))}
            className="min-h-10 rounded-full border border-line bg-surface px-3 text-sm"
          >
            <option value="">{t('cal.jump')}</option>
            {Array.from({ length: 12 }, (_, i) => i + 1).map((n) => (
              <option key={n} value={n}>
                {n === 1 ? t('cal.jumpWeek') : t('cal.jumpWeeks', { n })}
              </option>
            ))}
          </select>
          {waiting.length > 0 && (
            <button onClick={() => setPanel({ mode: 'waitlist' })} className="inline-flex min-h-10 items-center gap-2 rounded-full border border-accent bg-accent-soft px-4 text-sm font-medium text-accent-strong hover:bg-accent/20">
              {t('waitlist.chip', { n: waiting.length })}
            </button>
          )}
        </div>
      </header>

      <ErrorNote message={cal.error ? (cal.error instanceof ApiError ? cal.error.message : t('error.generic')) : null} />
      {/* Wide screens: the agenda with a column beside it (month, the day in numbers, open gaps). */}
      <div className="flex flex-col gap-4 2xl:grid 2xl:grid-cols-[minmax(0,1fr)_320px] 2xl:items-start 2xl:gap-6">
      <div className="min-w-0">
      {!data ? (
        <p className="py-10 text-center text-muted">{t('cal.loading')}</p>
      ) : data.staff.length === 0 ? (
        <EmptyState title={t('nav.agenda')} body={t('cal.noTeam')} />
      ) : view === 'lijst' ? (
        <ListView data={data} days={days} tz={tz} staffFilter={isPhone ? undefined : search.wie} onOpen={(a) => setPanel({ mode: 'view', id: a.id })} />
      ) : (
        <CalendarGrid
          data={data}
          days={days}
          view={view}
          tz={tz}
          today={today}
          now={now}
          isPhone={isPhone}
          person={person}
          staffFilter={search.wie}
          onPerson={(id) => setSearch({ wie: id })}
          onDay={(d) => setSearch({ datum: d === today ? undefined : d, weergave: undefined })}
          draft={draft}
          onSlot={(c, start, end) => openCreate({ date: c.date, minute: start, minutes: end === null ? undefined : end - start, staffId: c.staffId })}
          onOpen={(a) => setPanel({ mode: 'view', id: a.id })}
          onOpenBlock={(b) => setPanel({ mode: 'block', block: b })}
          onMove={handleMove}
          onSwipe={isPhone && view === 'dag' ? step : undefined}
        />
      )}
      </div>
      <div className="hidden 2xl:block 2xl:max-h-[calc(100dvh-170px)] 2xl:overflow-y-auto">
        <DayColumn
          data={data}
          days={days}
          view={view}
          date={date}
          today={today}
          tz={tz}
          now={now}
          showRevenue={role !== 'staff'}
          staffIds={dayStaffIds}
          onPickDate={goDate}
          onSlot={(staffId, d, minute) => openCreate({ date: d, minute, staffId })}
        />
      </div>
      </div>

      {panel && location.data && (
        <SidePanel
          open
          title={
            panel.mode === 'create'
              ? t(panel.tab === 'block' ? 'appt.tab.block' : 'appt.newTitle')
              : panel.mode === 'edit'
                ? t('appt.editTitle')
                : panel.mode === 'view'
                  ? t('appt.viewTitle')
                  : panel.mode === 'waitlist'
                    ? t('waitlist.title')
                    : t('appt.tab.block')
          }
          onClose={closePanel}
        >
          {panel.mode === 'waitlist' && <WaitlistList orgId={orgId} locationId={location.data.id} date={date} entries={waiting} />}
          {panel.mode === 'create' && (
            <div className="flex flex-col gap-5">
              <div role="tablist" className="grid grid-cols-2 rounded-full border border-line bg-surface p-0.5">
                {(['appointment', 'block'] as const).map((tab) => (
                  <button
                    key={tab}
                    role="tab"
                    aria-selected={panel.tab === tab}
                    onClick={() => setPanel({ ...panel, tab })}
                    className={`min-h-10 rounded-full text-sm font-medium ${panel.tab === tab ? 'bg-ink text-canvas' : 'text-muted hover:text-ink'}`}
                  >
                    {t(`appt.tab.${tab}`)}
                  </button>
                ))}
              </div>
              {panel.tab === 'appointment' ? (
                <AppointmentForm
                  orgId={orgId}
                  locationId={location.data.id}
                  tz={tz}
                  staff={staff}
                  role={role}
                  prefill={panel.prefill}
                  onDraft={setDraft}
                  onDone={(a) => {
                    setPanel(null);
                    const d = dateOf(a.startsAt, tz);
                    if (!days.includes(d)) goDate(d);
                    showToast(t('toast.created'));
                  }}
                />
              ) : (
                <BlockForm
                  orgId={orgId}
                  locationId={location.data.id}
                  tz={tz}
                  staff={staff}
                  role={role}
                  ownStaffId={ownStaffId}
                  prefill={panel.prefill}
                  onDraft={setDraft}
                  onDone={(affected) => {
                    if (affected.length > 0) setPanel({ mode: 'affected', appointments: affected });
                    else {
                      setPanel(null);
                      showToast(t('block.saved'));
                    }
                  }}
                />
              )}
            </div>
          )}
          {panel.mode === 'view' && (
            <AppointmentView
              orgId={orgId}
              id={panel.id}
              tz={tz}
              staff={staff}
              onEdit={(a) => setPanel({ mode: 'edit', id: a.id })}
              onNext={(prefill) => {
                setPanel({ mode: 'create', tab: 'appointment', prefill });
                goDate(prefill.date);
              }}
              onToast={showToast}
            />
          )}
          {panel.mode === 'edit' && (
            <EditAppointment
              orgId={orgId}
              id={panel.id}
              locationId={location.data.id}
              tz={tz}
              staff={staff}
              role={role}
              onDraft={setDraft}
              onDone={(a) => {
                setPanel({ mode: 'view', id: a.id });
                const d = dateOf(a.startsAt, tz);
                if (!days.includes(d)) goDate(d);
              }}
            />
          )}
          {panel.mode === 'block' && (
            <BlockView
              orgId={orgId}
              block={panel.block}
              tz={tz}
              staff={staff}
              canDelete={role !== 'staff' || (panel.block.staffMemberId !== null && panel.block.staffMemberId === ownStaffId)}
              onDone={closePanel}
            />
          )}
          {panel.mode === 'affected' && <AffectedAppointments orgId={orgId} tz={tz} staff={staff} appointments={panel.appointments} onDone={closePanel} />}
        </SidePanel>
      )}

      {forceAsk && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-ink/30 px-4" role="dialog" aria-modal="true" aria-label={t('appt.forceTitle')}>
          <div className="flex w-full max-w-sm flex-col gap-3 rounded-[var(--radius-card)] bg-surface p-5 shadow-xl">
            <p className="text-lg font-semibold">{t('appt.forceTitle')}</p>
            <p className="text-muted">{forceAsk.message}</p>
            <div className="flex flex-wrap gap-2">
              <Button
                onClick={() => {
                  forceAsk.retry();
                  setForceAsk(null);
                }}
              >
                {t('appt.force')}
              </Button>
              <Button variant="ghost" onClick={() => setForceAsk(null)}>
                {t('common.cancel')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {toast && <ToastBar key={toast.id} toast={toast} onClose={() => setToast(null)} />}
    </div>
  );
}

function EditAppointment({ orgId, id, locationId, tz, staff, role, onDone, onDraft }: { orgId: string; id: string; locationId: string; tz: string; staff: StaffOption[]; role: 'owner' | 'manager' | 'staff'; onDone: (a: AppointmentDetail) => void; onDraft: (d: Draft | null) => void }) {
  const t = useT();
  const appt = useAppointment(orgId, id);
  if (!appt.data) return <p className="text-muted">{t('common.loading')}</p>;
  const a = appt.data;
  const c = a.customer;
  return (
    <AppointmentForm
      orgId={orgId}
      locationId={locationId}
      tz={tz}
      staff={staff}
      role={role}
      prefill={{
        date: dateOf(a.startsAt, tz),
        minute: minuteOfDay(a.startsAt, tz),
        staffId: a.items[0]?.staffMemberId ?? null,
        items: a.items.map((i) => ({ serviceId: i.serviceId, variantId: i.variantId })),
      }}
      editing={{
        id: a.id,
        version: a.version,
        note: a.internalNote,
        customer: c ? { id: c.id, firstName: c.firstName, lastName: c.lastName, phone: c.phone, email: c.email, alertNote: c.alertNote } : null,
      }}
      onDone={onDone}
      onDraft={onDraft}
    />
  );
}

function ToastBar({ toast, onClose }: { toast: Toast; onClose: () => void }) {
  const t = useT();
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    const id = window.setTimeout(() => closeRef.current(), toast.undo ? 10_000 : 4000);
    return () => window.clearTimeout(id);
  }, [toast]);
  return (
    <div role="status" className="fixed inset-x-4 bottom-24 z-[70] mx-auto flex max-w-md items-center justify-between gap-3 rounded-[var(--radius-card)] bg-ink px-4 py-3 text-[15px] text-surface shadow-lg md:bottom-6">
      <span>{toast.message}</span>
      {toast.secondary && (
        <button
          className="shrink-0 text-sm text-surface/80 underline-offset-2 hover:underline"
          onClick={() => {
            toast.secondary?.run();
            onClose();
          }}
        >
          {toast.secondary.label}
        </button>
      )}
      {toast.undo && (
        <button
          className="shrink-0 font-semibold text-accent-soft underline-offset-2 hover:underline"
          onClick={() => {
            toast.undo?.();
            onClose();
          }}
        >
          {t('toast.undo')}
        </button>
      )}
    </div>
  );
}

// ---------- Day and week grid ----------

function CalendarGrid({
  data,
  days,
  view,
  tz,
  today,
  now,
  isPhone,
  person,
  staffFilter,
  onPerson,
  onDay,
  draft,
  onSlot,
  onOpen,
  onOpenBlock,
  onMove,
  onSwipe,
}: {
  data: CalendarData;
  days: string[];
  view: 'dag' | 'week';
  tz: string;
  today: string;
  now: number;
  isPhone: boolean;
  person: string | undefined;
  staffFilter: string | undefined;
  onPerson: (id: string) => void;
  onDay: (date: string) => void;
  draft: Draft | null;
  onSlot: (c: GridColumn, start: number, end: number | null) => void;
  onOpen: (a: CalendarAppointment) => void;
  onOpenBlock: (b: CalendarBlock) => void;
  onMove: (m: MoveRequest) => void;
  onSwipe?: (dir: 1 | -1) => void;
}) {
  const t = useT();
  const locale = useLocale();

  const closedLabel = (date: string) => {
    const c = data.closedDays.find((d) => d.date === date);
    if (!c) return undefined;
    return c.holidayName ? c.holidayName[locale] : (c.message ?? t('cal.closed'));
  };

  // Which columns: phones and week view show one person; day view shows who works (or a filter).
  const columns: GridColumn[] = useMemo(() => {
    const byId = new Map(data.staff.map((s) => [s.id, s]));
    if (view === 'week' || isPhone) {
      const s = person ? byId.get(person) : undefined;
      if (!s) return [];
      return days.map((date) => ({
        key: `${s.id}:${date}`,
        date,
        staffId: s.id,
        color: s.color,
        working: rangesOnDate(s.scheduled, date, tz),
        closedLabel: closedLabel(date),
        isToday: date === today,
        header:
          view === 'week' ? (
            <button
              type="button"
              onClick={() => onDay(date)}
              className={`rounded-full px-3 py-1 text-sm font-semibold first-letter:uppercase ${date === today ? 'bg-accent text-on-accent' : 'hover:bg-subtle'}`}
            >
              {formatDateShort(date, locale)}
            </button>
          ) : (
            <PersonSwitcher staff={data.staff} value={s.id} onChange={onPerson} />
          ),
      }));
    }
    const date = days[0]!;
    const busyIds = new Set(data.appointments.filter((a) => dateOf(a.startsAt, tz) === date).flatMap((a) => a.staffIds));
    let shown = data.staff.filter((s) => rangesOnDate(s.scheduled, date, tz).length > 0 || busyIds.has(s.id));
    if (staffFilter) shown = data.staff.filter((s) => s.id === staffFilter);
    if (shown.length === 0) shown = data.staff;
    return shown.map((s) => ({
      key: `${s.id}:${date}`,
      date,
      staffId: s.id,
      color: s.color,
      working: rangesOnDate(s.scheduled, date, tz),
      closedLabel: closedLabel(date) ?? (rangesOnDate(s.scheduled, date, tz).length === 0 ? t('cal.notWorking') : undefined),
      isToday: date === today,
      header: (
        <StaffHeader
          name={s.displayName}
          color={s.color}
          hours={rangesOnDate(s.scheduled, date, tz)
            .map((r) => `${hhmm(r.start)}–${hhmm(r.end)}`)
            .join(', ')}
          count={data.appointments.filter((a) => a.staffIds.includes(s.id) && dateOf(a.startsAt, tz) === date && a.status !== 'cancelled').length}
        />
      ),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, days.join(), view, isPhone, person, staffFilter, today, locale]);

  const items: GridItem[] = useMemo(() => {
    const out: GridItem[] = [];
    for (const a of data.appointments) {
      const date = dateOf(a.startsAt, tz);
      for (const sid of a.staffIds) {
        const col = columns.find((c) => c.staffId === sid && c.date === date);
        if (!col) continue;
        const parts = a.parts
          .filter((p) => p.staffMemberId === sid)
          .map((p) => ({ kind: p.kind, start: minuteIn(p.start, date, tz), end: minuteIn(p.end, date, tz) }));
        const client = parts.filter((p) => p.kind === 'active' || p.kind === 'processing');
        const start = client.length ? Math.min(...client.map((p) => p.start)) : minuteIn(a.startsAt, date, tz);
        const end = client.length ? Math.max(...client.map((p) => p.end)) : minuteIn(a.endsAt, date, tz);
        out.push({ appointment: a, columnKey: col.key, start, end, parts });
      }
    }
    return out;
  }, [data, columns, tz]);

  const blocks: GridBlock[] = useMemo(
    () =>
      data.blocks.flatMap((b) =>
        columns
          .filter((c) => (b.staffMemberId === null || b.staffMemberId === c.staffId) && Date.parse(b.start) < Date.parse(instantAt(addDays(c.date, 1), 0, tz)) && Date.parse(b.end) > Date.parse(instantAt(c.date, 0, tz)))
          .map((c) => ({
            key: `${b.blockId}:${b.date}:${c.key}`,
            columnKey: c.key,
            start: Math.max(0, minuteIn(b.start, c.date, tz)),
            end: Math.min(1440, minuteIn(b.end, c.date, tz)),
            block: b,
          })),
      ),
    [data, columns, tz],
  );

  // Visible hours: the working day, widened to fit every appointment.
  const starts = [8 * 60, ...columns.flatMap((c) => c.working.map((w) => w.start)), ...items.map((i) => i.start)];
  const ends = [18 * 60, ...columns.flatMap((c) => c.working.map((w) => w.end)), ...items.map((i) => Math.max(...i.parts.map((p) => p.end), i.end))];
  const startMin = Math.max(0, Math.floor((Math.min(...starts) - 30) / 60) * 60);
  const endMin = Math.min(1440, Math.ceil((Math.max(...ends) + 30) / 60) * 60);
  const nowMin = days.includes(today) ? minuteOfDay(now, tz) : null;

  const preview: GridPreview | null =
    draft && draft.staffId ? { columnKey: `${draft.staffId}:${draft.date}`, start: draft.minute, end: draft.minute + draft.minutes, label: draft.label } : null;

  if (columns.length === 0) return <EmptyState title={t('nav.agenda')} body={t('cal.nobodyWorking')} />;

  return (
    <TimeGrid
      key={`${view}:${isPhone}`}
      columns={columns}
      items={items}
      blocks={blocks}
      startMin={startMin}
      endMin={endMin}
      nowMin={nowMin}
      preview={preview}
      minColumnWidth={view === 'week' ? (isPhone ? 96 : 120) : isPhone ? 240 : 160}
      onSlot={onSlot}
      onOpen={onOpen}
      onOpenBlock={onOpenBlock}
      onMove={onMove}
      onSwipe={onSwipe}
    />
  );
}

/** Day view column head: who, in their colour, with their hours and how busy they are. */
function StaffHeader({ name, color, hours, count }: { name: string; color: string; hours: string; count: number }) {
  const t = useT();
  return (
    <span className="flex flex-col items-center gap-0.5 py-0.5">
      <span className="flex items-center gap-2 text-[15px] font-semibold">
        <span className="flex size-6 items-center justify-center rounded-full text-[11px] font-semibold text-white" style={{ backgroundColor: color }}>
          {name.slice(0, 1).toUpperCase()}
        </span>
        {name}
      </span>
      <span className="text-[12px] tabular-nums text-muted">
        {hours || t('cal.notWorking')}
        {count > 0 && ` · ${count === 1 ? t('today.oneAppointment') : t('today.appointments', { n: count })}`}
      </span>
    </span>
  );
}

/** Phone header: tap the name to switch person. */
function PersonSwitcher({ staff, value, onChange }: { staff: CalendarData['staff']; value: string; onChange: (id: string) => void }) {
  const t = useT();
  const s = staff.find((x) => x.id === value);
  return (
    <label className="relative flex items-center gap-1.5 text-[15px] font-semibold">
      <span className="size-2.5 rounded-full" style={{ backgroundColor: s?.color }} />
      {s?.displayName}
      {staff.length > 1 && <span className="text-muted">▾</span>}
      {staff.length > 1 && (
        <select aria-label={t('cal.switchPerson')} value={value} onChange={(e) => onChange(e.target.value)} className="absolute inset-0 cursor-pointer opacity-0">
          {staff.map((x) => (
            <option key={x.id} value={x.id}>
              {x.displayName}
            </option>
          ))}
        </select>
      )}
    </label>
  );
}

// ---------- List ----------

function ListView({ data, days, tz, staffFilter, onOpen }: { data: CalendarData; days: string[]; tz: string; staffFilter?: string; onOpen: (a: CalendarAppointment) => void }) {
  const t = useT();
  const locale = useLocale();
  const name = (id: string) => data.staff.find((s) => s.id === id);
  return (
    <div className="flex flex-col gap-4">
      {days.map((date) => {
        const list = data.appointments.filter((a) => dateOf(a.startsAt, tz) === date && (!staffFilter || a.staffIds.includes(staffFilter)));
        const closed = data.closedDays.find((d) => d.date === date);
        return (
          <section key={date} className="flex flex-col gap-2">
            <h2 className="text-[15px] font-semibold first-letter:uppercase">
              {formatDateLong(date, locale)}
              {closed && <span className="ml-2 text-sm font-normal text-muted">{closed.holidayName?.[locale] ?? closed.message ?? t('cal.closed')}</span>}
            </h2>
            {list.length === 0 ? (
              <p className="text-sm text-muted">{t('cal.listEmpty')}</p>
            ) : (
              <ul className="flex flex-col overflow-hidden rounded-[var(--radius-card)] border border-line bg-surface">
                {list.map((a) => {
                  const s = name(a.staffIds[0]!);
                  return (
                    <li key={a.id} className="border-b border-line last:border-b-0">
                      <button className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-subtle" onClick={() => onOpen(a)}>
                        <span className="w-24 shrink-0 tabular-nums font-semibold">
                          {formatTime(a.startsAt, tz)}–{formatTime(a.endsAt, tz)}
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block truncate font-medium">
                            {a.customerName ?? (a.source === 'walk_in' ? t('cal.walkIn') : t('cal.noClient'))}
                            {a.newClient && ' ★'}
                            {a.alert && ' ⚠'}
                            {a.source === 'online' && ' 🌐'}
                          </span>
                          <span className="block truncate text-sm text-muted">{a.services.join(' + ')}</span>
                        </span>
                        <span className="flex shrink-0 items-center gap-1.5 text-sm text-muted">
                          <span className="size-2.5 rounded-full" style={{ backgroundColor: s?.color }} />
                          {s?.displayName}
                        </span>
                        {a.status !== 'booked' && (
                          <span className={`hidden shrink-0 rounded-full px-2 py-0.5 text-xs font-semibold sm:inline ${a.status === 'completed' ? 'bg-accent-soft text-accent-strong' : 'bg-danger-soft text-danger'}`}>
                            {t(`status.${a.status}` as TKey)}
                          </span>
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        );
      })}
    </div>
  );
}
